const MIN_WIDTH = 407;

/**
 * @typedef {object} ResizeConfig
 * @property {HTMLElement} domRef
 * @property {boolean} [startResizing]
 */

/**
 * Uses `data-resizable` attribute
 */
class Resizable {
  /**
   * @param {ResizeConfig} config
   */
  constructor (config) {
    const { domRef, startResizing = false } = config;

    /** @private */
    this.domRef = domRef;

    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerOut = this.handlePointerOut.bind(this);

    this.enableResizing(startResizing);
  }

  /**
   * @param {boolean} enabled
   */
  enableResizing (enabled) {
    this.domRef.setAttribute('data-resizable', enabled ? 'true' : 'false');

    // Always initially remove to prevent duplicated events
    this.domRef.ownerDocument.removeEventListener(
      'pointerdown',
      this.handlePointerDown
    );
    this.domRef.ownerDocument.removeEventListener(
      'pointermove',
      this.handlePointerMove
    );
    this.domRef.removeEventListener('pointerout', this.handlePointerOut);

    if (enabled) {
      this.domRef.ownerDocument.addEventListener(
        'pointerdown',
        this.handlePointerDown,
        { passive: true }
      );
      this.domRef.ownerDocument.addEventListener(
        'pointermove',
        this.handlePointerMove,
        { passive: true }
      );
      this.domRef.addEventListener('pointerout', this.handlePointerOut, {
        passive: true
      });
    }
  }

  /**
   * @private
   * @param {PointerEvent} event
   */
  handlePointerDown (event) {
    if (!this.onLeftBorder(event)) {
      return;
    }

    const {
      left: initialLeft,
      width: initialWidth
    } = this.domRef.getBoundingClientRect();

    /**
     * @param {PointerEvent} event
     */
    const innerPointerMove = event => {
      const newWidthOffset = event.clientX - initialLeft;

      const newWidth = initialWidth - newWidthOffset;

      this.domRef.style.width =
        Math.min(
          window.innerWidth - 300,
          Math.max(newWidth, MIN_WIDTH)
        ).toString() + 'px';

      this.domRef.ownerDocument.addEventListener(
        'pointerup',
        () => {
          this.domRef.ownerDocument.removeEventListener(
            'pointermove',
            innerPointerMove
          );
        },
        { once: true, passive: true }
      );
    };

    this.domRef.ownerDocument.addEventListener(
      'pointermove',
      innerPointerMove,
      {
        passive: true
      }
    );

    this.domRef.ownerDocument.addEventListener(
      'pointerup',
      () => {
        this.domRef.dispatchEvent(
          new CustomEvent('resizeend', { bubbles: true })
        );
      },
      { once: true, passive: true }
    );
  }

  /**
   * @private
   * @param {PointerEvent} event
   */
  handlePointerMove (event) {
    // @ts-ignore
    if (!event.target?.closest) {
      return;
    }

    const isPointerInside =
      // @ts-ignore
      event.target.closest('[data-resizable="true"]') === this.domRef;

    if (isPointerInside) {
      const isOnLeftBorder = this.onLeftBorder(event);

      if (isOnLeftBorder) {
        event.stopPropagation();
        this.domRef.ownerDocument.body.style.cursor = 'w-resize';
      } else {
        this.domRef.ownerDocument.body.style.cursor = 'auto';
      }
    }
  }

  /**
   * @private
   */
  handlePointerOut () {
    this.domRef.ownerDocument.body.style.cursor = 'auto';
  }

  /**
   * @private
   * @param {PointerEvent} event
   */
  onLeftBorder (event) {
    const { clientX, clientY } = event;
    const { left, top, height } = this.domRef.getBoundingClientRect();

    const minX = left;
    const maxX = left + 10;
    const minY = top;
    const maxY = top + height - 10;

    return (
      minX < clientX &&
      clientX < maxX &&
      minY < clientY &&
      clientY < maxY &&
      height > 30
    );
  }
}

export default Resizable;
